/* MIT License

Copyright (c) 2025 Brad Schick

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. */

import { Service } from '@angular/core';
import * as cc from '@qcrypt/crypto/consts';
import { Ciphers, encryptStream, decryptStream, getCipherStreamInfo, cryptoReady } from '@qcrypt/crypto';
import type { EContext, CipherDataInfo, CipherDone, KeyProvider } from '@qcrypt/crypto';

export type { EContext, CipherDataInfo, CipherDone };

const TARGET_HASH_MILLIS = 500;
const MAX_HASH_MILLIS = 5 * 60 * 1000; //5 minutes

/* Thin wrapper around Ciphers and cipher stream functions to create
an Angular service. Must of that functionality was previously in this
class directly, and perhaps this class could now be removed, but keeping
it for now to avoid having to update other code and many tests */

@Service()
export class CipherService {
   private _iCount = 0;
   private _hashRate = 0;
   private _iCountMax = cc.ICOUNT_MAX;

   get hashRate(): number {
      return this._hashRate;
   }

   async benchmark(testSize: number): Promise<[number, number, number]> {
      await navigator.locks.request('benchmark', async () => {
         if (this._iCount === 0) {
            await cryptoReady();
            [this._iCount, this._iCountMax, this._hashRate] = await Ciphers.benchmark(
               testSize,
               TARGET_HASH_MILLIS,
               MAX_HASH_MILLIS,
            );
         }
      });

      return [this._iCount, this._iCountMax, this._hashRate];
   }

   // This method purges the passed in KeyProvider
   async encryptStream(
      clearStream: ReadableStream<Uint8Array>,
      keyProvider: KeyProvider,
      econtext: EContext,
      onDone?: CipherDone,
   ): Promise<ReadableStream<Uint8Array>> {
      await cryptoReady();
      return encryptStream(clearStream, keyProvider, econtext, onDone);
   }

   // This method purges the passed in KeyProvider
   async getCipherStreamInfo(
      cipherStream: ReadableStream<Uint8Array>,
      keyProvider: KeyProvider,
   ): Promise<CipherDataInfo> {
      await cryptoReady();
      return getCipherStreamInfo(cipherStream, keyProvider);
   }

   // This method purges the passed in KeyProvider
   async decryptStream(
      cipherStream: ReadableStream<Uint8Array>,
      keyProvider: KeyProvider,
      onDone?: CipherDone,
   ): Promise<ReadableStream<Uint8Array>> {
      await cryptoReady();
      return decryptStream(cipherStream, keyProvider, onDone);
   }
}
